/**
 * orak-blocking.js
 * Orak — Product blocking system
 *
 * Handles:
 *   1. Modal open / close (focus trap, scroll lock, keyboard, aria)
 *   2. Block form validation and submission
 *   3. Extend block (prolongation)
 *   4. Acompte redirect
 *   5. Real-time UI update after successful block
 *   6. Init: binds all widgets found on the page (PDP + catalogue)
 *
 * Dependencies:
 *   - window.ORAK_APP_URL  — set via theme settings (e.g. "https://orak-app.fly.dev")
 *   - Liquid snippet block-button.liquid renders the markup
 *
 * Vanilla JS only. No jQuery. No build step required.
 */

(function () {
  'use strict';

  /* ============================================================
     CONSTANTS & CONFIGURATION
     ============================================================ */

  /** Fallback: if ORAK_APP_URL is not defined the fetch will hit /block
   *  on the same origin — which will 404 on a stock Shopify storefront.
   *  The mini-app MUST set window.ORAK_APP_URL in theme.liquid via:
   *    <script>window.ORAK_APP_URL = "{{ settings.orak_app_url }}";</script>
   */
  var APP_BASE_URL = (window.ORAK_APP_URL || '').replace(/\/$/, '');

  var ACOMPTE_PAGE = '/pages/reserve';

  /* Selectors — kept in one place for easy refactoring */
  var SEL = {
    widget        : '[id^="BlockWidget-"]',
    blockBtn      : '[id^="BlockButton-"]',
    modal         : '[id^="BlockModal-"]',
    form          : '[id^="BlockForm-"]',
    submitBtn     : '[id^="BlockSubmit-"]',
    extendBtn     : '[data-action="extend"]',
    acompteBtn    : '[data-action="acompte"]',
    modalClose    : '[data-modal-close]',
    backdrop      : '.orak-modal__backdrop',
    successBlock  : '.orak-block-success',
    errorBlock    : '.orak-block-error',
    errorMsg      : '.orak-block-error__message',
    chantierInput : '[id^="ChantierName-"]',
    chantierError : '[id^="ChantierError-"]',
  };

  /* ============================================================
     UTILITIES
     ============================================================ */

  /**
   * Returns the element for a given product ID and selector pattern.
   * @param {string} selector - CSS selector with no ID suffix
   * @param {string} productId
   * @returns {Element|null}
   */
  function byProductId(baseId, productId) {
    return document.getElementById(baseId + '-' + productId);
  }

  /**
   * Sets the [hidden] attribute on an element (hides it).
   * @param {Element} el
   */
  function hide(el) {
    if (el) el.setAttribute('hidden', '');
  }

  /**
   * Removes the [hidden] attribute from an element (shows it).
   * @param {Element} el
   */
  function show(el) {
    if (el) el.removeAttribute('hidden');
  }

  /**
   * Locks body scroll by storing current scrollY and applying overflow:hidden.
   * Stores an instance counter to support multiple open modals in edge cases.
   */
  var _scrollLockCount = 0;
  var _scrollLockY = 0;

  function lockBodyScroll() {
    if (_scrollLockCount === 0) {
      _scrollLockY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + _scrollLockY + 'px';
      document.body.style.width = '100%';
    }
    _scrollLockCount++;
  }

  function unlockBodyScroll() {
    _scrollLockCount = Math.max(0, _scrollLockCount - 1);
    if (_scrollLockCount === 0) {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, _scrollLockY);
    }
  }

  /**
   * Returns all focusable elements within a container, filtered to visible ones.
   * @param {Element} container
   * @returns {Element[]}
   */
  function getFocusableElements(container) {
    var candidates = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), ' +
      'select:not([disabled]), textarea:not([disabled]), ' +
      '[tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(candidates, function (el) {
      return !el.closest('[hidden]') && !el.hasAttribute('hidden');
    });
  }

  /**
   * Traps focus within `container` while it is open.
   * Returns a cleanup function to unbind the listener.
   * @param {Element} container
   * @returns {Function} cleanup
   */
  function trapFocus(container) {
    function handler(e) {
      if (e.key !== 'Tab') return;
      var focusable = getFocusableElements(container);
      if (!focusable.length) return;
      var first = focusable[0];
      var last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handler);
    return function () {
      document.removeEventListener('keydown', handler);
    };
  }

  /* ============================================================
     MODAL MANAGEMENT
     ============================================================ */

  /** Map of productId → { cleanupFocusTrap, triggerElement } */
  var _openModals = {};

  /**
   * Opens the block modal for the given product.
   * @param {string} productId
   */
  function openBlockModal(productId) {
    var modal    = byProductId('BlockModal', productId);
    var trigger  = byProductId('BlockButton', productId);
    var input    = modal ? modal.querySelector('[id^="ChantierName-"]') : null;

    if (!modal) return;

    // Reset modal state before showing
    resetModal(productId);

    show(modal);
    lockBodyScroll();

    // Aria on trigger
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    // Focus first input field after transition paint
    if (input) {
      requestAnimationFrame(function () {
        input.focus();
      });
    }

    // Trap focus
    var cleanupTrap = trapFocus(modal.querySelector('.orak-modal__dialog'));

    _openModals[productId] = {
      cleanupFocusTrap : cleanupTrap,
      triggerElement   : trigger,
    };
  }

  /**
   * Closes the block modal for the given product.
   * @param {string} productId
   */
  function closeBlockModal(productId) {
    var modal   = byProductId('BlockModal', productId);
    var trigger = byProductId('BlockButton', productId);
    var state   = _openModals[productId];

    if (!modal) return;

    hide(modal);
    unlockBodyScroll();

    // Cleanup focus trap
    if (state && typeof state.cleanupFocusTrap === 'function') {
      state.cleanupFocusTrap();
    }

    // Restore focus to trigger
    var returnTarget = (state && state.triggerElement) ? state.triggerElement : trigger;
    if (returnTarget) {
      returnTarget.setAttribute('aria-expanded', 'false');
      returnTarget.focus();
    }

    delete _openModals[productId];
  }

  /**
   * Resets modal to its initial form state (clears inputs, hides success/error).
   * @param {string} productId
   */
  function resetModal(productId) {
    var modal = byProductId('BlockModal', productId);
    if (!modal) return;

    var form        = byProductId('BlockForm', productId);
    var success     = modal.querySelector('.orak-block-success');
    var error       = modal.querySelector('.orak-block-error');
    var submitBtn   = byProductId('BlockSubmit', productId);
    var chantierInput = modal.querySelector('[id^="ChantierName-"]');
    var chantierErr   = modal.querySelector('[id^="ChantierError-"]');

    if (form) {
      show(form);
      form.reset();
    }
    hide(success);
    hide(error);

    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.classList.remove('is-loading');
      var btnText    = submitBtn.querySelector('.btn__text');
      var btnLoading = submitBtn.querySelector('.btn__loading');
      if (btnText)    show(btnText);
      if (btnLoading) hide(btnLoading);
    }

    if (chantierInput) chantierInput.classList.remove('is-error');
    if (chantierErr)   chantierErr.textContent = '';
  }

  /* ============================================================
     VALIDATION
     ============================================================ */

  /**
   * Validates the block form for a given product.
   * Returns true if valid, false otherwise (also updates inline error UI).
   * @param {string} productId
   * @returns {boolean}
   */
  function validateBlockForm(productId) {
    var modal         = byProductId('BlockModal', productId);
    var form          = byProductId('BlockForm', productId);
    var surfaceInput  = modal ? modal.querySelector('[id^="BlockSurface-"]') : null;
    var surfaceError  = modal ? modal.querySelector('[id^="SurfaceError-"]') : null;
    var chantierInput = modal ? modal.querySelector('[id^="ChantierName-"]') : null;
    var chantierError = modal ? modal.querySelector('[id^="ChantierError-"]') : null;

    // ── Surface (m²) ──
    if (surfaceInput) {
      surfaceInput.classList.remove('is-error');
      if (surfaceError) surfaceError.textContent = '';

      var raw = String(surfaceInput.value || '').trim().replace(',', '.');
      var surface = Number(raw);
      var available = form ? Number(form.getAttribute('data-available')) : NaN;

      if (!raw) {
        setFieldError(surfaceInput, surfaceError, 'Indiquez la surface à bloquer (m²).');
        return false;
      }
      if (!isFinite(surface) || surface <= 0) {
        setFieldError(surfaceInput, surfaceError, 'La surface doit être un nombre de m² positif.');
        return false;
      }
      if (isFinite(available) && available > 0 && surface > available) {
        setFieldError(surfaceInput, surfaceError, 'Il ne reste que ' + available + ' m² disponibles.');
        return false;
      }
    }

    // ── Chantier ──
    if (!chantierInput) return false;

    var value = chantierInput.value.trim();

    // Reset previous error
    chantierInput.classList.remove('is-error');
    if (chantierError) chantierError.textContent = '';

    if (!value) {
      setFieldError(chantierInput, chantierError, 'Le nom du chantier est obligatoire.');
      return false;
    }
    if (value.length < 3) {
      setFieldError(chantierInput, chantierError, 'Le nom du chantier doit contenir au moins 3 caractères.');
      return false;
    }
    if (value.length > 100) {
      setFieldError(chantierInput, chantierError, 'Le nom du chantier ne peut pas dépasser 100 caractères.');
      return false;
    }

    return true;
  }

  /**
   * Marks an input as erroneous and sets the accompanying error message.
   * @param {HTMLInputElement} input
   * @param {Element|null} errorEl
   * @param {string} message
   */
  function setFieldError(input, errorEl, message) {
    input.classList.add('is-error');
    if (errorEl) errorEl.textContent = message;
    input.focus();
  }

  /* ============================================================
     SUBMIT BUTTON STATE
     ============================================================ */

  function setSubmitLoading(productId, isLoading) {
    var submitBtn  = byProductId('BlockSubmit', productId);
    if (!submitBtn) return;

    var btnText    = submitBtn.querySelector('.btn__text');
    var btnLoading = submitBtn.querySelector('.btn__loading');

    if (isLoading) {
      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
      if (btnText)    hide(btnText);
      if (btnLoading) show(btnLoading);
    } else {
      submitBtn.disabled = false;
      submitBtn.classList.remove('is-loading');
      if (btnText)    show(btnText);
      if (btnLoading) hide(btnLoading);
    }
  }

  /* ============================================================
     API CALL — BLOCK / EXTEND
     ============================================================ */

  /**
   * Posts a block or extend request to the Orak mini-app.
   *
   * Endpoint: POST {ORAK_APP_URL}/block
   * Body (JSON):
   *   product_id    {string}
   *   customer_id   {string}
   *   chantier_name {string}
   *   product_price {string}  — raw Shopify price in cents
   *   action        {string}  — "block" | "extend"
   *
   * The mini-app is responsible for:
   *   - Writing the four orak.* metafields via the Admin API
   *   - Triggering Shopify Flow (webhook trigger or Flow HTTP action)
   *   - Sending the confirmation email
   *
   * Returns a plain object: { ok: boolean, error?: string }
   *
   * @param {Object} payload
   * @returns {Promise<{ok: boolean, error?: string}>}
   */
  async function callBlockAPI(payload) {
    var url    = APP_BASE_URL + '/block';

    try {
      var response = await fetch(url, {
        method  : 'POST',
        headers : {
          'Content-Type' : 'application/json',
        },
        body    : JSON.stringify(payload),
      });

      // Try to parse JSON regardless of status code
      var data        = null;
      var contentType = response.headers.get('content-type') || '';
      if (contentType.indexOf('application/json') !== -1) {
        try { data = await response.json(); } catch (_) { /* ignore parse error */ }
      }

      // Validate response shape before trusting any property
      if (!data || typeof data !== 'object') {
        return { ok: false, error: 'Réponse invalide du serveur. Veuillez réessayer.' };
      }

      if (!response.ok) {
        // Use server-provided message if available, otherwise a generic one
        var serverMsg = (typeof data.error === 'string' && data.error)
          ? data.error
          : ('Erreur serveur (' + response.status + '). Veuillez réessayer.');
        return { ok: false, error: serverMsg };
      }

      // Successful response must contain success: true
      if (data.success !== true) {
        return { ok: false, error: (typeof data.error === 'string' ? data.error : 'Erreur serveur.') };
      }

      return { ok: true, data: data };

    } catch (networkError) {
      // Network failure (offline, CORS, DNS)
      console.error('[Orak Blocking] Network error:', networkError);
      return {
        ok    : false,
        error : 'Impossible de contacter le serveur. Vérifiez votre connexion internet et réessayez.',
      };
    }
  }

  /* ============================================================
     FORM SUBMISSION — BLOCK
     ============================================================ */

  /**
   * Handles the block form submission for a given product.
   * Validates, calls the API, then updates the UI.
   * @param {string} productId
   * @param {Event} e - form submit event
   */
  async function handleBlockSubmit(productId, e) {
    e.preventDefault();

    if (!validateBlockForm(productId)) return;

    var form    = byProductId('BlockForm', productId);
    var modal   = byProductId('BlockModal', productId);
    var formData = new FormData(form);

    var payload = {
      product_id    : formData.get('product_id'),
      customer_id   : formData.get('customer_id'),
      chantier_name : (formData.get('chantier_name') || '').trim(),
      surface_m2    : String(formData.get('surface_m2') || '').trim().replace(',', '.'),
      product_price : formData.get('product_price'),
      action        : 'block',
    };

    setSubmitLoading(productId, true);

    var result = await callBlockAPI(payload);

    setSubmitLoading(productId, false);

    if (result.ok) {
      // Show success panel inside the modal
      hide(form);
      var successEl = modal ? modal.querySelector('.orak-block-success') : null;
      if (successEl) {
        show(successEl);
        // Move focus to success heading for screen readers
        var successTitle = successEl.querySelector('.orak-block-success__title');
        if (successTitle) {
          successTitle.setAttribute('tabindex', '-1');
          successTitle.focus();
        }
      }

      // Reload so the server-rendered state (remaining m² + your block cards)
      // reflects the new reservation.
      setTimeout(function () { window.location.reload(); }, 1400);

    } else {
      // Show error panel inside the modal
      var errorEl  = modal ? modal.querySelector('.orak-block-error')        : null;
      var errorMsg = modal ? modal.querySelector('.orak-block-error__message') : null;
      if (errorEl && errorMsg) {
        errorMsg.textContent = result.error;
        show(errorEl);
      }
    }
  }

  /* ============================================================
     REAL-TIME UI UPDATE — replace block button with owned state
     ============================================================ */

  /**
   * After a successful block, replaces the block button area with the
   * "blocked by you" info box, without requiring a page reload.
   *
   * NOTE: This creates a client-side optimistic UI. The actual metafield
   * values are persisted server-side. On next page load the Liquid template
   * renders the correct server state.
   *
   * @param {string} productId
   * @param {string} chantierName
   */
  function updateWidgetToOwned(productId, chantierName) {
    var actionContainer = document.getElementById('BlockWidgetAction-' + productId);
    if (!actionContainer) return;

    // Calculate expiry date: today + 15 days
    var expiry = new Date();
    expiry.setDate(expiry.getDate() + 15);
    var expiryStr = expiry.toLocaleDateString('fr-FR', {
      day   : '2-digit',
      month : '2-digit',
      year  : 'numeric',
    });

    var ownedHTML = [
      '<div class="orak-block-widget__owned" id="BlockWidgetOwned-' + productId + '">',
      '  <div class="orak-block-info">',
      '    <div class="orak-block-info__header">',
      '      <span class="orak-block-badge orak-block-badge--owned">Bloqué par vous</span>',
      '    </div>',
      '    <div class="orak-block-info__meta">',
      '      <p class="orak-block-info__line">',
      '        <span class="orak-block-info__label">Expire le</span>',
      '        <span class="orak-block-info__value">' + expiryStr + '</span>',
      '      </p>',
      chantierName ? (
        '      <p class="orak-block-info__line">' +
        '        <span class="orak-block-info__label">Chantier</span>' +
        '        <span class="orak-block-info__value">' + escapeHTML(chantierName) + '</span>' +
        '      </p>'
      ) : '',
      '    </div>',
      '    <div class="orak-block-info__actions">',
      '      <button class="btn btn-extend" type="button" data-action="extend"',
      '        data-product-id="' + productId + '">',
      '        Prolonger (15 jours)',
      '      </button>',
      '      <button class="btn btn-acompte" type="button" data-action="acompte"',
      '        data-product-id="' + productId + '">',
      '        Réserver avec acompte 20\u00A0%',
      '      </button>',
      '    </div>',
      '  </div>',
      '</div>',
    ].join('\n');

    actionContainer.outerHTML = ownedHTML;

    // Re-bind the newly inserted buttons
    var ownedContainer = document.getElementById('BlockWidgetOwned-' + productId);
    if (ownedContainer) {
      bindExtendButton(ownedContainer.querySelector('[data-action="extend"]'));
      bindAcompteButton(ownedContainer.querySelector('[data-action="acompte"]'));
    }
  }

  /**
   * Minimal HTML escape for user-supplied strings inserted via innerHTML.
   * @param {string} str
   * @returns {string}
   */
  function escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ============================================================
     EXTEND (PROLONGATION)
     ============================================================ */

  /**
   * Handles the "Prolonger" button click.
   * Asks for confirmation, then calls the API with action: "extend".
   * On success updates the displayed expiry date in the widget.
   * @param {string} productId
   */
  async function handleExtend(productId, blockId, extendBtn) {
    var confirmed = window.confirm(
      'Prolonger ce blocage pour 15 jours supplémentaires ?'
    );
    if (!confirmed) return;

    // customer_id comes from the block form hidden field (present whenever the
    // block CTA is rendered). If the reference is now full there is no form, so
    // fall back to the widget's data — the server re-verifies ownership anyway.
    var form = byProductId('BlockForm', productId);

    var payload = {
      product_id : productId,
      customer_id: form ? form.querySelector('[name="customer_id"]').value : '',
      block_id   : blockId,
      action     : 'extend',
    };

    if (extendBtn) {
      extendBtn.disabled = true;
      extendBtn.textContent = 'Prolongation…';
    }

    var result = await callBlockAPI(payload);

    if (result.ok) {
      showToast('Blocage prolongé de 15 jours.');
      setTimeout(function () { window.location.reload(); }, 900);
    } else {
      if (extendBtn) {
        extendBtn.disabled = false;
        extendBtn.textContent = 'Prolonger (15 jours)';
      }
      showToast(result.error || 'Erreur lors de la prolongation.', true);
    }
  }

  /* ============================================================
     ACOMPTE REDIRECT
     ============================================================ */

  /**
   * Redirects to the acompte reservation page.
   * The mini-app page reads ?product_id= and creates the payment link.
   * @param {string} productId
   */
  function handleAcompte(productId) {
    window.location.href = ACOMPTE_PAGE + '?product_id=' + encodeURIComponent(productId);
  }

  /* ============================================================
     TOAST NOTIFICATION (lightweight, no extra library)
     ============================================================ */

  var _toastTimeout = null;

  /**
   * Shows a brief toast message at the bottom of the viewport.
   * Auto-dismisses after 4 seconds.
   * @param {string} message
   * @param {boolean} [isError=false]
   */
  function showToast(message, isError) {
    var existing = document.getElementById('orak-toast');
    if (existing) existing.remove();
    if (_toastTimeout) clearTimeout(_toastTimeout);

    var toast = document.createElement('div');
    toast.id = 'orak-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.style.cssText = [
      'position:fixed',
      'bottom:24px',
      'left:50%',
      'transform:translateX(-50%)',
      'z-index:9999',
      'background:' + (isError ? '#1a0a0a' : '#141414'),
      'border:1px solid ' + (isError ? 'rgba(255,68,68,0.4)' : 'rgba(170,202,184,0.4)'),
      'color:' + (isError ? '#ff6b6b' : '#AACAB8'),
      'font-family:"Outfit",sans-serif',
      'font-size:13px',
      'font-weight:600',
      'letter-spacing:0.04em',
      'padding:12px 24px',
      'max-width:90vw',
      'text-align:center',
      'pointer-events:none',
      'opacity:0',
      'transition:opacity 0.25s',
    ].join(';');

    toast.textContent = message;
    document.body.appendChild(toast);

    // Fade in
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        toast.style.opacity = '1';
      });
    });

    _toastTimeout = setTimeout(function () {
      toast.style.opacity = '0';
      setTimeout(function () { toast.remove(); }, 300);
    }, 4000);
  }

  /* ============================================================
     EVENT BINDING HELPERS
     ============================================================ */

  /**
   * Binds a single extend button.
   * @param {Element|null} btn
   */
  function bindExtendButton(btn) {
    if (!btn) return;
    btn.addEventListener('click', function () {
      var productId = btn.dataset.productId;
      var blockId   = btn.dataset.blockId;
      if (productId && blockId) handleExtend(productId, blockId, btn);
    });
  }

  /**
   * Binds a single acompte button.
   * @param {Element|null} btn
   */
  function bindAcompteButton(btn) {
    if (!btn) return;
    btn.addEventListener('click', function () {
      var productId = btn.dataset.productId;
      if (productId) handleAcompte(productId);
    });
  }

  /* ============================================================
     KEYBOARD HANDLING — Escape closes modal
     ============================================================ */

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    // Close the most recently opened modal
    var openIds = Object.keys(_openModals);
    if (openIds.length > 0) {
      closeBlockModal(openIds[openIds.length - 1]);
    }
  });

  /* ============================================================
     INIT
     Runs on DOMContentLoaded. Safe to call multiple times (idempotent
     via dataset flag to avoid double-binding on dynamic page updates).
     ============================================================ */

  function init() {
    /* ── 1. Block buttons (open modal) ── */
    document.querySelectorAll('[id^="BlockButton-"]').forEach(function (btn) {
      if (btn.dataset.orBlocked) return;
      btn.dataset.orBlocked = '1';
      btn.addEventListener('click', function () {
        var productId = btn.dataset.productId;
        if (productId) openBlockModal(productId);
      });
    });

    /* ── 2. Modal close triggers (backdrop, × button, cancel button) ── */
    document.querySelectorAll('[data-modal-close]').forEach(function (el) {
      if (el.dataset.orBound) return;
      el.dataset.orBound = '1';
      el.addEventListener('click', function () {
        var productId = el.dataset.productId;
        if (productId) closeBlockModal(productId);
      });
    });

    /* ── 3. Block forms ── */
    document.querySelectorAll('[id^="BlockForm-"]').forEach(function (form) {
      if (form.dataset.orBound) return;
      form.dataset.orBound = '1';
      var productId = form.id.replace('BlockForm-', '');
      form.addEventListener('submit', function (e) {
        handleBlockSubmit(productId, e);
      });
      // Clear field errors on input
      var chantierInput = form.querySelector('[id^="ChantierName-"]');
      if (chantierInput) {
        chantierInput.addEventListener('input', function () {
          chantierInput.classList.remove('is-error');
          var errorEl = form.querySelector('[id^="ChantierError-"]');
          if (errorEl) errorEl.textContent = '';
        });
      }
      var surfaceInput = form.querySelector('[id^="BlockSurface-"]');
      if (surfaceInput) {
        surfaceInput.addEventListener('input', function () {
          surfaceInput.classList.remove('is-error');
          var errorEl = form.querySelector('[id^="SurfaceError-"]');
          if (errorEl) errorEl.textContent = '';
        });
      }
    });

    /* ── 4. Extend buttons ── */
    document.querySelectorAll('[data-action="extend"]').forEach(function (btn) {
      if (btn.dataset.orBound) return;
      btn.dataset.orBound = '1';
      bindExtendButton(btn);
    });

    /* ── 5. Acompte buttons ── */
    document.querySelectorAll('[data-action="acompte"]').forEach(function (btn) {
      if (btn.dataset.orBound) return;
      btn.dataset.orBound = '1';
      bindAcompteButton(btn);
    });
  }

  /* ============================================================
     BOOT
     ============================================================ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already parsed (script loaded with defer or at bottom of body)
    init();
  }

  /* ── Public API (optional, exposed for theme customisations) ── */
  window.OrakBlocking = {
    open       : openBlockModal,
    close      : closeBlockModal,
    reinit     : init,
    showToast  : showToast,
  };

})();
